import {
  getPatientFullName,
  type listExistingPatientWithHomeVisitFlagSchema,
  patientStatusSchema,
} from "@medoma/models/patients";
import type { z } from "zod";

type PatientWithHomeVisitFlag = z.infer<
  typeof listExistingPatientWithHomeVisitFlagSchema
>;

/**
 * Builds the GeoJSON feature collection of active patients for the planning map.
 * Pure projection of the data the map needs; see PlanningMap for how the layers
 * consume the resulting properties.
 */
export const buildPatientFeatures = ({
  patients,
  selectedPatientId,
  patientIdsFromSelectedRoute,
}: {
  patients: PatientWithHomeVisitFlag[];
  selectedPatientId: string | null | undefined;
  patientIdsFromSelectedRoute: string[];
}) => ({
  type: "FeatureCollection" as const,
  // Discharged patients are fetched so a clicked visit can still highlight
  // them, but they don't get a dot of their own on the map.
  features: patients
    .filter(
      (patient) =>
        patient.status !== patientStatusSchema.enum.discharged ||
        patient.id === selectedPatientId,
    )
    .map((patient) => ({
      type: "Feature" as const,
      properties: {
        name: getPatientFullName(patient.name),
        patient_id: patient.id,
        // hasHomeVisit is already narrowed to the active time window by the
        // PlanningMap query's select; route membership is irrelevant here.
        has_home_visit: patient.hasHomeVisit,
        examined_patient_name:
          patient.id === selectedPatientId
            ? getPatientFullName(patient.name)
            : null,
        in_selected_route: patientIdsFromSelectedRoute.includes(patient.id),
      },
      geometry: {
        type: "Point" as const,
        coordinates: [
          patient.address.coordinates.longitude,
          patient.address.coordinates.latitude,
        ],
      },
    })),
});
